import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { EmptyIndicator, Pagination } from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import { StatCount } from 'components/common/stats';
import {
  AchievementUsersHeader,
  AchievementPaths,
  AchievementCompletionsAccessRequests,
  AchievementUsersLinkedCalendarEvents,
  AchievementUsersSubHeader,
  AchievementUsersTable
} from '.';

import type { GetAchievementUsersRes } from 'shared-types/responses';
import type { AchievementUsersView } from './AchievementUsersSubHeader';

const AchievementUsers = () => {
  const {
    messages,
    pageState: { achievement },
    serverConfig
  } = useReduxState();

  const router = useRouter();
  const queryKeys = Object.keys(router.query);
  const focusedUserId = router.query.focusedUserId;

  const { data, loading, updateData } = useApiData<GetAchievementUsersRes>({
    url:
      `/admin/achievements/${achievement.id}/users` +
      (queryKeys.length > 0
        ? `?${queryKeys
            .filter((key) => key !== 'focusedUserId')
            .map((key) => `${key}=${router.query[key]}`)
            .join('&')}`
        : '')
  });

  const handleBackClick = () => {
    const queryParams = { ...router.query };
    delete queryParams.focusedUserId;

    router.replace({ query: queryParams }, undefined, { shallow: true });
  };

  const updateUsers = async () => {
    if (router.query.page !== '1') {
      router.replace({ query: { ...router.query, page: 1 } });
    } else await updateData();
  };

  return (
    <div className="users">
      <AchievementCompletionsAccessRequests updateUsers={updateUsers} />

      <Box
        headerContent={
          <AchievementUsersHeader
            alreadyAddedUsersByEmail={data?.alreadyAddedUsersByEmail || {}}
            alreadyAddedUsersByUsername={
              data?.alreadyAddedUsersByUsername || {}
            }
            listOfGroupCompletionIds={[]}
            listOfUserIds={data?.listOfUserIds || null}
            usersWithInProgressAchievementByEmail={
              data?.usersWithInProgressAchievementByEmail || {}
            }
            usersWithInProgressAchievementByUsername={
              data?.usersWithInProgressAchievementByUsername || {}
            }
            updateUsers={updateUsers}
          />
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        subHeaderContent={<AchievementUsersSubHeader />}
        title={
          focusedUserId ? (
            <BoxBackLink
              onBackClick={handleBackClick}
              simpleReturn={true}
              text={messages.usersAllUsers}
            />
          ) : (
            <>
              {messages.commonUsers}

              <StatCount
                isLarge={true}
                style={{ marginLeft: 5 }}
                value={data?.listOfUserIds?.length || 0}
              />
            </>
          )
        }
      >
        {data?.users?.length ? (
          <AchievementUsersTable
            users={
              focusedUserId
                ? data.users
                    .filter((user) => user.id === Number(focusedUserId))
                    .map(({ completions, ...user }) =>
                      completions.map((completion) => ({
                        ...user,
                        completions: [completion]
                      }))
                    )
                    .flat()
                : data.users
            }
            updateUsers={updateUsers}
            view={(router.query.view as AchievementUsersView) || 'default'}
          />
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </Box>

      {focusedUserId ? null : (
        <>
          <Pagination
            currentPage={router.query.page ? Number(router.query.page) : 1}
            numberOfPages={data?.pages || 1}
            onPageChange={({ page }) => {
              router.replace({ query: { ...router.query, page } });
            }}
          />

          <AchievementUsersLinkedCalendarEvents />

          {!serverConfig.hidePathsFromUI && <AchievementPaths />}
        </>
      )}
    </div>
  );
};

export default AchievementUsers;
